/**
 * API Client — Single source of truth for all API requests
 *
 * - HttpOnly cookie auth (credentials: 'include')
 * - Uses relative paths (Vite proxy in dev, same-origin in prod)
 * - Centralized error handling: 401, 402, 5xx, network errors
 * - Auto-reports 5xx errors to backend for CloudWatch logging
 */

import { reportError } from "@/lib/errorReporter";
import { getEmbedToken } from "@/lib/embedSession";
import {
  SESSION_REPLACED_MESSAGE,
  announceSessionReplaced,
  isSessionReplacedBody,
} from "@/lib/sessionReplaced";
import { EMBED_MARKER_HEADER } from "@touchworks/shared/platform/embedProtocol";

/**
 * Custom error for API responses
 */
export class ApiError extends Error {
  constructor(message, status, data = null) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.data = data;
  }
}

/**
 * Fetch wrapper with credentials and centralized error handling
 */
/** Lê o corpo JSON de uma resposta sem consumi-la (o chamador ainda pode ler). Corpo ausente ou inválido vira `{}`. */
async function readJsonBody(response) {
  try {
    return (await response.clone().json()) ?? {};
  } catch {
    return {};
  }
}

export async function apiFetch(endpoint, options = {}) {
  const url = endpoint.startsWith("http") ? endpoint : endpoint;

  // Generate a unique request ID for end-to-end correlation (frontend → API → workers)
  const requestId = crypto.randomUUID();

  // EMB-09: quando o shell embutido guarda um token, toda requisição carrega
  // o bearer + o marcador que faz o backend preferi-lo a um cookie velho
  // (ver apps/api/middleware/auth.js getTokenFromRequest). Sem token setado
  // (app normal), este bloco não adiciona nada — comportamento idêntico ao
  // de sempre.
  const embedToken = getEmbedToken();
  const config = {
    ...options,
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      "x-request-id": requestId,
      ...(embedToken && {
        Authorization: `Bearer ${embedToken}`,
        [EMBED_MARKER_HEADER]: "1",
      }),
      ...options.headers,
    },
  };

  // Remove Content-Type for FormData (let browser set it with boundary)
  if (options.body instanceof FormData) {
    delete config.headers["Content-Type"];
  }

  let response;
  try {
    response = await fetch(url, config);
  } catch (error) {
    if (error.name === "AbortError") throw error;
    throw new ApiError(
      "Sem conexão com o servidor. Verifique sua internet.",
      0,
    );
  }

  // 429 — rate limited → não retenta, apenas propaga
  if (response.status === 429) {
    throw new ApiError(
      "Muitas requisições. Aguarde alguns segundos.",
      429,
    );
  }

  // 401 — sessão expirada → sinaliza para AuthContext (sem hard reload)
  if (response.status === 401) {
    // Sessão substituída por um login mais novo (conta por licença): mesmo caminho, com o motivo para o aviso do login
    const body = await readJsonBody(response);
    if (isSessionReplacedBody(body)) {
      announceSessionReplaced();
      throw new ApiError(SESSION_REPLACED_MESSAGE, 401, body);
    }
    // Dispara evento para que o AuthContext revogue a sessão via SPA navigation
    // Evita window.location.href que causa remount infinito
    window.dispatchEvent(new CustomEvent('auth:expired'));
    throw new ApiError("Sessão expirada", 401);
  }

  // 402 — conta bloqueada → billing (BlockedAccountOverlay cuida da UI)
  if (response.status === 402) {
    const data = await response.json().catch(() => ({}));
    // Dispatch event so AuthContext updates billingStatus immediately
    window.dispatchEvent(new CustomEvent('billing:blocked', { detail: data }));
    throw new ApiError(
      "Seu acesso está temporariamente indisponível. Contate o administrador da sua empresa.",
      402,
      data,
    );
  }

  // 403 SEAT_BLOCKED — seat limit exceeded (SeatBlockDialog cuida da UI)
  if (response.status === 403) {
    const data = await response.json().catch(() => ({}));
    if (data.code === 'SEAT_BLOCKED') {
      throw new ApiError(
        data.message || "Limite de usuários excedido",
        403,
        data,
      );
    }
    if (data.code === 'RESOURCE_LIMIT' && data.resource === 'users') {
      // Dispara o dialog de upgrade de seats e retorna response para o caller tratar
      globalThis.dispatchEvent(
        new CustomEvent('seat:upgrade_required', {
          detail: {
            limit: data.limit,
            current: data.current,
            excess: Math.max(1, (data.current || 0) - (data.limit || 0) + 1),
          },
        })
      );
    }
    if (
      data.code === 'RESOURCE_LIMIT' &&
      (data.resource === 'whatsapp_numbers' ||
        data.resource === 'channels.whatsapp_numbers')
    ) {
      // Dispara o dialog de upgrade de números WhatsApp
      globalThis.dispatchEvent(
        new CustomEvent('whatsapp:upgrade_required', {
          detail: {
            limit: data.limit,
            current: data.current,
            excess: Math.max(1, (data.current || 0) - (data.limit || 0) + 1),
          },
        })
      );
    }
    // Other 403s (permissions etc.) — return response for caller to handle
    // Re-create the response since we consumed json()
    return new Response(JSON.stringify(data), {
      status: 403,
      statusText: response.statusText,
      headers: { 'Content-Type': 'application/json' },
    });
  }

  // 5xx — erro do servidor
  if (response.status >= 500) {
    const data = await response.json().catch(() => ({}));
    // A deliberate failure carries a `code` and a message written for the person (a legal system that did not
    // answer: 502 PROVIDER_UNAVAILABLE); an unexpected 500 only has the developer's text, which stays hidden.
    const message = data.message || (data.code && data.error) || "Erro interno do servidor";
    const error = new ApiError(message, response.status, data);

    // Auto-report 5xx errors to backend for monitoring
    reportError("api.5xx", error, {
      endpoint: url,
      method: config.method || "GET",
      status: response.status,
      responseData: data,
    });

    throw error;
  }

  return response;
}

/**
 * GET request
 */
export async function apiGet(endpoint) {
  return apiFetch(endpoint);
}

/**
 * POST request with JSON body
 */
export async function apiPost(endpoint, data) {
  return apiFetch(endpoint, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

/**
 * PUT request with JSON body
 */
export async function apiPut(endpoint, data) {
  return apiFetch(endpoint, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

/**
 * DELETE request
 */
export async function apiDelete(endpoint) {
  return apiFetch(endpoint, {
    method: "DELETE",
  });
}

/**
 * POST request with FormData (file upload)
 */
export async function apiUpload(endpoint, formData) {
  return apiFetch(endpoint, {
    method: "POST",
    body: formData,
  });
}

export default { apiFetch, apiGet, apiPost, apiPut, apiDelete, apiUpload };
